/**
 * The Shift Roster's own reads: each line's shifts and each line's home crew for
 * a plant-week, loaded once per plant-week and kept across re-mounts.
 */
import { addDays, format } from "date-fns";
import { useEffect, useState } from "react";

import { productionPlanApi } from "../../../../../services/production-plan-api";
import rosterAssignmentsApi, { HomeCrewMember } from "../../../../../services/roster-assignments-api";
import { LineShift } from "../../../production-plan/utils/line-shifts";

interface CrewData {
	key: string;
	shifts: LineShift[];
	crews: HomeCrewMember[];
}

const NO_SHIFTS: LineShift[] = [];
const NO_CREWS: HomeCrewMember[] = [];

/**
 * A plant-week's line shifts and home crews, loaded once and kept: the board
 * re-mounts whenever the roster reloads its week, and a request must not be
 * dropped (and started again) each time.
 */
const crewCache = new Map<string, { data?: CrewData; pending?: Promise<CrewData> }>();

function loadCrewData(locationId: number, weekKey: string): Promise<CrewData> {
	const key = `${locationId}|${weekKey}`;
	const known = crewCache.get(key);
	if (known?.data) return Promise.resolve(known.data);
	if (known?.pending) return known.pending;
	const to = format(addDays(new Date(`${weekKey}T00:00:00`), 8), "yyyy-MM-dd");
	const pending = Promise.all([
		productionPlanApi.getLineShifts(locationId, weekKey, to).catch(() => NO_SHIFTS),
		rosterAssignmentsApi.getHomeCrews(locationId, weekKey, to).catch(() => NO_CREWS),
	]).then(([shifts, crews]) => {
		const data = { key, shifts, crews };
		// An empty answer (a failed call) is not kept, so the next visit asks again.
		crewCache.set(key, shifts.length || crews.length ? { data } : {});
		return data;
	});
	crewCache.set(key, { pending });
	return pending;
}

/** The plant-week's line shifts and home crews (empty until loaded). */
export function useCrewData(locationId: number, weekStart: Date): { shifts: LineShift[]; crews: HomeCrewMember[] } {
	// Keyed on the week's date, not the Date object: the board hands a new one each render.
	const weekKey = format(weekStart, "yyyy-MM-dd");
	const cacheKey = `${locationId}|${weekKey}`;
	const [data, setData] = useState<CrewData | null>(() => crewCache.get(cacheKey)?.data ?? null);
	useEffect(() => {
		if (!locationId) return undefined;
		let mounted = true;
		void loadCrewData(locationId, weekKey).then((loaded) => {
			if (mounted) setData(loaded);
		});
		return () => {
			mounted = false;
		};
	}, [locationId, weekKey]);
	return data && data.key === cacheKey ? data : { shifts: NO_SHIFTS, crews: NO_CREWS };
}
